/**
 * Confirm Results logic for a multi-location reservation (issue #421).
 *
 * A single-location event has always produced one flat `import` map and one number to
 * confirm. A multi-location event produces up to ~90 per-location results, any of which
 * can be missing, still running or permanently failed — so "what is the total, and is it
 * safe to confirm it" stops being obvious and becomes a decision worth testing.
 *
 * Everything here is pure so it can be unit tested with `node --test`, matching the
 * convention of services/reservationEditPermissions.js and services/eventTime.js.
 * components/reservation/results.js and importLocations.js render from these.
 */

/**
 * Merge an event type doc UNDERNEATH a reservation's own `event` map.
 *
 * The reservation's `event` is an OVERRIDE snapshot, not a cache: the payout callable
 * (apiEventReservationPayoutUpdated) writes the per-reservation `payout`/`payoutType`
 * into it. The store's `getDocument` join REPLACES the field wholesale, which discarded
 * that override — and results.js computes `sales * (event.payout/100)`, so the page
 * showed a donation at the event TYPE's rate.
 *
 * Only keys the override actually defines shadow the event type, so a partially-written
 * override cannot blank out event-type fields (minThresholdResults, allDay, starts…) the
 * page still needs.
 *
 * Mirrors utilities/toastSalesImport.js:mergeReservationEventOverride on the backend,
 * which fixes the same defect in utilities/common.js:getReservationDetails. Duplicated
 * rather than shared because functions/ is outside the pnpm workspace.
 */
const mergeReservationEventOverride = (eventTypeData, storedEvent) => {
  const base = eventTypeData || {};
  if (!storedEvent || typeof storedEvent !== "object" || Array.isArray(storedEvent)) {
    return { ...base };
  }
  const merged = { ...base };
  Object.keys(storedEvent).forEach((key) => {
    const value = storedEvent[key];
    if (value === undefined || value === null) return;
    merged[key] = value;
  });
  return merged;
};

// The per-location statuses the backend can report.
//
// PENDING vs NOT_IMPORTED is the important distinction, and it is not cosmetic. Pending
// means an import run is still open and this location will resolve on its own, so
// confirming now would post a number that is about to change. NOT_IMPORTED means every
// run has closed without producing a job for it — a failed fan-out, most likely — so
// nothing further is coming and treating it as pending would leave the customer waiting
// on nothing, unable to confirm, forever.
const IMPORT_STATUS = Object.freeze({
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  SKIPPED: "skipped",
  BLOCKED: "blocked",
  NOT_IMPORTED: "not_imported",
  PENDING: "pending",
  QUEUED: "queued",
  RUNNING: "running",
});

const PENDING_STATUSES = [IMPORT_STATUS.PENDING, IMPORT_STATUS.QUEUED, IMPORT_STATUS.RUNNING];

// Missing, and no longer moving. These are the rows that make a total partial for good.
const TERMINAL_MISSING_STATUSES = [IMPORT_STATUS.FAILED, IMPORT_STATUS.BLOCKED, IMPORT_STATUS.NOT_IMPORTED];

// What a customer needs to know about each status, without Toast vocabulary.
const STATUS_LABELS = Object.freeze({
  [IMPORT_STATUS.SUCCEEDED]: { label: "Imported", variant: "success" },
  [IMPORT_STATUS.FAILED]: { label: "Failed", variant: "danger" },
  [IMPORT_STATUS.SKIPPED]: { label: "Skipped", variant: "secondary" },
  [IMPORT_STATUS.BLOCKED]: { label: "Not set up", variant: "warning" },
  [IMPORT_STATUS.NOT_IMPORTED]: { label: "Not imported", variant: "danger" },
  [IMPORT_STATUS.PENDING]: { label: "Pending", variant: "info" },
  [IMPORT_STATUS.QUEUED]: { label: "Pending", variant: "info" },
  [IMPORT_STATUS.RUNNING]: { label: "Importing", variant: "info" },
});

const statusLabel = (status) => STATUS_LABELS[status] || { label: status || "Unknown", variant: "secondary" };

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Pull an endpoint payload out of Firebase's callable response and this repo's shared
 * `{id, data}` API envelope.
 *
 * `httpsCallable` returns `{data: {id, data: payload}}`. Keeping the outer envelope in
 * Redux means `hasMultiLocationImport` never sees `payload.locations`, so the entire
 * multi-location results panel stays hidden even though the callable succeeded.
 */
const unwrapImportResultsResponse = (response) => response?.data?.data || null;

/**
 * Build the raw Toast-order query for either the existing single-location breakdown or
 * a selected row in the multi-location import table.
 *
 * Multi-location reservations can contain thousands of raw orders across roughly 90
 * restaurants. Adding the location predicate lets the drill-down load only the orders
 * the customer asked to inspect. firestore.indexes.json contains the matching
 * reservationID + locationID composite index.
 */
const toastOrderQueryWhere = (reservationID, locationID) => {
  const filters = [["reservationID", "==", reservationID]];
  if (locationID) filters.push(["locationID", "==", locationID]);
  return filters;
};

/**
 * Roll a per-location list up into the numbers the aggregate panel shows.
 *
 * `complete` is the load-bearing one. It is NOT "did every job finish" — a location that
 * was never set up in Toast has no job and never will, so a definition based on jobs
 * would report a reservation as complete while its total silently covered 80 of 90
 * restaurants. It is "did every location on the reservation report a result".
 */
const summarizeImportLocations = (locations) => {
  const list = Array.isArray(locations) ? locations : [];

  const counts = {
    total: list.length,
    succeeded: 0,
    failed: 0,
    skipped: 0,
    blocked: 0,
    notImported: 0,
    pending: 0,
  };

  let sales = 0;
  let donation = 0;
  let transactions = 0;

  list.forEach((location) => {
    const status = location?.status;
    if (status === IMPORT_STATUS.SUCCEEDED) {
      counts.succeeded++;
      sales += Number(location.sales) || 0;
      donation += Number(location.donation) || 0;
      transactions += Number(location.transactions) || 0;
    } else if (status === IMPORT_STATUS.FAILED) counts.failed++;
    else if (status === IMPORT_STATUS.SKIPPED) counts.skipped++;
    else if (status === IMPORT_STATUS.BLOCKED) counts.blocked++;
    else if (status === IMPORT_STATUS.NOT_IMPORTED) counts.notImported++;
    else if (PENDING_STATUSES.includes(status)) counts.pending++;
  });

  // A skipped location DID report: it is legitimately not a Total Sales location, so it
  // contributes nothing and is not missing. Everything else that is not a success is.
  const missing = counts.failed + counts.blocked + counts.notImported + counts.pending;
  const reported = counts.succeeded + counts.skipped;

  return {
    counts,
    reported,
    missing,
    sales: round2(sales),
    donation: round2(donation),
    transactions,
    complete: counts.total > 0 && missing === 0,
    // Still moving on its own. Distinguishes "wait, this will resolve itself" from
    // "this is as good as it will get without someone fixing something".
    inFlight: counts.pending > 0,
  };
};

/**
 * Can this multi-location result be confirmed, and does confirming need an explicit
 * acknowledgement first?
 *
 * A partial total is a real, defensible thing to confirm — some locations may genuinely
 * never import, and the fundraiser still needs paying. What is NOT acceptable is
 * confirming a partial total without being told it is partial, because the number looks
 * exactly like a complete one. So a partial result is confirmable only after the
 * acknowledgement, and a still-running import is not confirmable at all: the number is
 * about to change on its own.
 */
const importConfirmState = (summary, acknowledged) => {
  if (!summary || !summary.counts.total) {
    return { blocked: false, needsAcknowledgement: false, canConfirm: true, reason: null };
  }
  if (summary.inFlight) {
    return {
      blocked: true,
      needsAcknowledgement: false,
      canConfirm: false,
      reason:
        `${summary.counts.pending} of ${summary.counts.total} locations are still importing. ` +
        `The total below will change — refresh in a few minutes.`,
    };
  }
  if (!summary.complete) {
    return {
      blocked: false,
      needsAcknowledgement: true,
      canConfirm: !!acknowledged,
      reason:
        `This total covers ${summary.reported} of ${summary.counts.total} locations. ` +
        `${summary.missing} did not report and are not included.`,
    };
  }
  return { blocked: false, needsAcknowledgement: false, canConfirm: true, reason: null };
};

/**
 * Filter and sort the per-location rows.
 *
 * Sorted so the rows a customer has to DO something about are not buried on page four of
 * ninety: everything that failed to report first, then by sales descending. Matching is
 * on name/city/state only — never on the failure text, or typing "failed" would match
 * rows by their error message rather than by the status filter.
 */
const filterImportLocations = (locations, { query, status } = {}) => {
  const list = Array.isArray(locations) ? locations : [];
  const needle = (query || "").trim().toLowerCase();

  const filtered = list.filter((location) => {
    if (status && status !== "all") {
      if (status === "missing") {
        if (![...TERMINAL_MISSING_STATUSES, ...PENDING_STATUSES].includes(location.status)) return false;
      } else if (status === "pending") {
        if (!PENDING_STATUSES.includes(location.status)) return false;
      } else if (location.status !== status) return false;
    }
    if (!needle) return true;
    return [location.name, location.city, location.state]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });

  const rank = (location) => {
    if (location.status === IMPORT_STATUS.FAILED) return 0;
    if (location.status === IMPORT_STATUS.NOT_IMPORTED) return 1;
    if (location.status === IMPORT_STATUS.BLOCKED) return 2;
    if (PENDING_STATUSES.includes(location.status)) return 3;
    return 4;
  };

  return filtered.slice().sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    const bySales = (Number(b.sales) || 0) - (Number(a.sales) || 0);
    if (bySales !== 0) return bySales;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
};

/**
 * Does this reservation use the multi-location import at all?
 *
 * Keyed on the response's own location count rather than on the reservation's `groupID`:
 * a group reservation whose locations were never fanned out has nothing to show, and a
 * one-location response has nothing a per-location table adds over the existing form.
 *
 * `applicable === false` is checked FIRST and explicitly. The server decides whether a
 * reservation is a Toast multi-location import at all — a non-Toast brand's reservation is
 * not one, however many locations it runs at (#473). Reading that intent from the length of
 * `locations` is what let an all-blocked row set render as a real nine-location import, so
 * the flag is honoured on its own terms rather than inferred from an empty array. Absent
 * (older payload) still means applicable, so this stays backward compatible.
 */
const hasMultiLocationImport = (importResults) =>
  !!importResults &&
  importResults.applicable !== false &&
  Array.isArray(importResults.locations) &&
  importResults.locations.length > 1;

// The client-visible Toast marker on a `locations/{id}` doc.
//
// The restaurant GUID itself lives in `location_secrets/{id}.toast_guid`, which has no
// firestore.rules entry and is therefore server-only — the dashboard can never read it.
// What it CAN read is this boolean, written alongside the secret by the admin app
// (components/location/editLocation.jsx sets `secrets[key] = true` when the GUID is
// saved), and already used one screen over to decide whether to offer the single-location
// Toast order breakdown. Verified in production: 0 Panera locations carry it, 92 do
// platform-wide — all of them the one brand that actually imports through Toast.
const TOAST_LOCATION_FLAG = "TOAST_GUID";

// Mirrors AUTO_MAX_LOCATIONS in functions/utilities/toastSalesImport.js. Duplicated for
// the same reason mergeReservationEventOverride is: functions/ is outside the workspace.
const PREVIEW_MAX_LOCATIONS = 150;

const DAY_PATTERN = /^\d{8}$/;

/**
 * Should Confirm Results EXPECT a multi-location Toast import, before the callable answers?
 *
 * `hasMultiLocationImport` reads the server's verdict; this predicts it, so the page can
 * show a loader over the region that verdict will fill. That matters because
 * apiGetEventReservationImportResults runs at minInstances=0 and fans out across ~90
 * restaurants — a real import took about 30 seconds, during which the page rendered a
 * settled-looking $0.00 sales form with no indication anything was still coming.
 *
 * Waiting for the response to learn whether to show a loader is circular. Waiting for a
 * cheap eligibility round-trip instead just moves the cold start. So this answers from
 * data `loadReservation` has ALREADY loaded: a group reservation resolves its `locations`
 * to full location docs (store/slices/reservation.js), and each carries the Toast flag.
 *
 * It re-derives the client-computable half of the server's own gate — the shape checks in
 * qualifyAutoImport (more than one location, exactly one business day, under the cap) plus
 * a per-location stand-in for the brand-credentials half of qualifyImportResultsView.
 *
 * Deliberately conservative, because the two ways of being wrong are not symmetrical:
 *
 *  - A false POSITIVE shows a loader to someone who will then be told there is no import —
 *    the #473 defect in miniature. So the Toast flag is REQUIRED, not assumed.
 *  - A false NEGATIVE only skips the loader; the response still renders normally when it
 *    lands. Historical and retried imports are the case that relies on this: the server
 *    returns those on evidence (a batch or job exists) even when the reservation no longer
 *    qualifies, and nothing here can see that evidence. Such a reservation simply waits
 *    without a spinner, exactly as it does today, and its panel still appears.
 *
 * The server's `applicable` flag remains authoritative in both directions.
 */
const expectsMultiLocationImport = (reservation) => {
  if (!reservation) return false;

  const locations = Array.isArray(reservation.locations) ? reservation.locations : [];
  if (locations.length < 2 || locations.length > PREVIEW_MAX_LOCATIONS) return false;

  // Multi-day multi-location reservations are excluded from the fan-out by design, and
  // this is the shape the Panera report arrived in. Both the flag and the stored date are
  // checked, matching qualifyAutoImport: they are written by different code paths, and a
  // disagreement means the shape is not understood — a reason to decline, not to guess.
  if (reservation.multiDay === true) return false;
  if (!DAY_PATTERN.test(String(reservation.eventDate || ""))) return false;
  if (Array.isArray(reservation.eventDates) && reservation.eventDates.length > 1) return false;

  return locations.some((location) => !!location?.secrets?.[TOAST_LOCATION_FLAG]);
};

/**
 * Should Confirm Results hold a loader over the sales-breakdown region right now?
 *
 * The whole decision, kept here rather than inlined into the component, so the states
 * that matter can be asserted directly — this module exists so results.js renders from
 * tested logic instead of carrying it.
 *
 * Resolution is uniform across every way the call can end. The loader is tied to the
 * pending flag, which the slice clears on `fulfilled` AND on `rejected`, so a success, an
 * inapplicable `applicable:false`, an empty result and an error all drop it; only success
 * with rows then raises the panel. It also yields as soon as results are on screen, so a
 * background refresh never replaces a table the customer is already reading with a
 * spinner.
 */
const shouldShowImportLoader = ({ reservation, loadingImportResults, importResults } = {}) =>
  !!loadingImportResults &&
  !hasMultiLocationImport(importResults) &&
  expectsMultiLocationImport(reservation);

module.exports = {
  IMPORT_STATUS,
  PENDING_STATUSES,
  TERMINAL_MISSING_STATUSES,
  expectsMultiLocationImport,
  filterImportLocations,
  hasMultiLocationImport,
  importConfirmState,
  mergeReservationEventOverride,
  shouldShowImportLoader,
  statusLabel,
  summarizeImportLocations,
  toastOrderQueryWhere,
  unwrapImportResultsResponse,
};
